import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { motion } from 'framer-motion';
import { SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SEO data for each category
const categorySEO = {
  'yeezy-350-v2': {
    title: 'Yeezy 350 V2 România | DEPRO KICKS – Magazin Sneakers Premium',
    description: 'Descoperă colecția completă Adidas Yeezy 350 V2 în România. Cele mai populare modele Yeezy: Zebra, Bone, Beluga, Bred și multe altele. Livrare rapidă, retur garantat.',
    keywords: 'yeezy 350 v2, yeezy romania, adidas yeezy, yeezy zebra, yeezy bone, yeezy beluga, yeezy bred, magazin yeezy'
  },
  'yeezy-380': {
    title: 'Yeezy 380 România | DEPRO KICKS',
    description: 'Adidas Yeezy 380 disponibil în România la DEPRO KICKS. Design unic, confort premium. Livrare rapidă.',
    keywords: 'yeezy 380, adidas yeezy 380, yeezy romania'
  },
  'yeezy-500': {
    title: 'Yeezy 500 România | DEPRO KICKS',
    description: 'Adidas Yeezy 500 disponibil în România. Sneakers premium cu design retro. Livrare rapidă la DEPRO KICKS.',
    keywords: 'yeezy 500, adidas yeezy 500, yeezy romania'
  },
  'yeezy-700': {
    title: 'Yeezy 700 România | DEPRO KICKS',
    description: 'Adidas Yeezy 700 în România la DEPRO KICKS. Modele Wave Runner și alte variante. Livrare rapidă, retur garantat.',
    keywords: 'yeezy 700, yeezy wave runner, adidas yeezy 700, yeezy romania'
  },
  'louboutin': {
    title: 'Louboutin Sneakers România | DEPRO KICKS',
    description: 'Christian Louboutin sneakers în România. Modele exclusive cu talpă roșie signature. Lux și eleganță la DEPRO KICKS.',
    keywords: 'louboutin, christian louboutin romania, louboutin sneakers, louboutin talpa rosie'
  },
  'alexander-mcqueen': {
    title: 'Alexander McQueen Sneakers România | DEPRO KICKS',
    description: 'Alexander McQueen Oversized sneakers în România. Design iconic, calitate premium la DEPRO KICKS.',
    keywords: 'alexander mcqueen, mcqueen sneakers, mcqueen oversized, mcqueen romania'
  },
  default: {
    title: 'Magazin Sneakers Premium | DEPRO KICKS România',
    description: 'Magazin online de sneakers premium în România. Yeezy 350 V2, Louboutin, Alexander McQueen. Livrare rapidă, retur garantat.',
    keywords: 'sneakers romania, yeezy, louboutin, alexander mcqueen, magazin sneakers, depro kicks'
  }
};

const ShopPage = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  
  // Filters
  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [reflectiveOnly, setReflectiveOnly] = useState(false);

  // Get SEO data for current category
  const seo = categorySEO[category] || categorySEO.default;

  const sizes = [36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46];
  
  const categories = [
    { slug: 'yeezy-350-v2', name: 'Yeezy 350 V2', count: 32 },
    { slug: 'yeezy-380', name: 'Yeezy 380', count: 1 },
    { slug: 'yeezy-500', name: 'Yeezy 500', count: 1 },
    { slug: 'yeezy-700', name: 'Yeezy 700', count: 3 },
    { slug: 'louboutin', name: 'Louboutin', count: 8 },
    { slug: 'alexander-mcqueen', name: 'Alexander McQueen', count: 4 },
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (priceRange[0] > 0) params.append('min_price', priceRange[0]);
        if (priceRange[1] < 2000) params.append('max_price', priceRange[1]);
        if (selectedSizes.length === 1) params.append('size', selectedSizes[0]);
        if (reflectiveOnly) params.append('reflective', 'true');
        
        const res = await axios.get(`${API}/products?${params.toString()}`);
        // Filter by selected sizes client-side if multiple selected
        let filtered = res.data;
        if (selectedSizes.length > 1) {
          filtered = filtered.filter(p => 
            selectedSizes.some(s => p.sizes.includes(s))
          );
        }
        setProducts(filtered);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, priceRange, selectedSizes, reflectiveOnly]);

  const toggleSize = (size) => {
    setSelectedSizes(prev => 
      prev.includes(size) 
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };

  const clearFilters = () => {
    setPriceRange([0, 2000]);
    setSelectedSizes([]);
    setReflectiveOnly(false);
  };

  const categoryTitle = category 
    ? categories.find(c => c.slug === category)?.name || 'Magazin'
    : 'Toate Produsele';

  const FilterContent = () => (
    <div className="space-y-8">
      {/* Categories */}
      <div>
        <h3 className="font-heading font-bold text-sm tracking-wider mb-4 text-white">CATEGORIE</h3>
        <div className="space-y-2">
          <a 
            href="/shop"
            className={`block font-body text-sm py-2 transition-colors ${!category ? 'text-[#c00000]' : 'text-white/60 hover:text-white'}`}
          >
            Toate Produsele
          </a>
          {categories.map(cat => (
            <a 
              key={cat.slug}
              href={`/shop/${cat.slug}`}
              className={`block font-body text-sm py-2 transition-colors ${category === cat.slug ? 'text-[#c00000]' : 'text-white/60 hover:text-white'}`}
            >
              {cat.name} <span className="text-white/30">({cat.count})</span>
            </a>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="font-heading font-bold text-sm tracking-wider mb-4 text-white">INTERVAL PREȚ</h3>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          min={0}
          max={2000}
          step={50}
          className="mb-4"
        />
        <div className="flex justify-between font-body text-sm text-white/60">
          <span>{priceRange[0]} RON</span>
          <span>{priceRange[1]} RON</span>
        </div>
      </div>

      {/* Sizes */}
      <div>
        <h3 className="font-heading font-bold text-sm tracking-wider mb-4 text-white">MĂRIME (EU)</h3>
        <div className="grid grid-cols-4 gap-2">
          {sizes.map(size => (
            <button
              key={size}
              onClick={() => toggleSize(size)}
              className={`py-2 font-body text-sm border transition-colors ${
                selectedSizes.includes(size)
                  ? 'border-[#c00000] bg-[#c00000]/10 text-[#c00000]'
                  : 'border-white/10 text-white/60 hover:border-white/30'
              }`}
              data-testid={`size-filter-${size}`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {/* Reflective */}
      <div>
        <h3 className="font-heading font-bold text-sm tracking-wider mb-4 text-white">TIP</h3>
        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={reflectiveOnly}
            onCheckedChange={setReflectiveOnly}
            className="border-white/30 data-[state=checked]:bg-[#c00000] data-[state=checked]:border-[#c00000]"
          />
          <span className="font-body text-sm text-white/60">Doar Reflective</span>
        </label>
      </div>

      {/* Clear Filters */}
      {(selectedSizes.length > 0 || reflectiveOnly || priceRange[0] > 0 || priceRange[1] < 2000) && (
        <Button
          onClick={clearFilters}
          variant="outline"
          className="w-full bg-transparent border-white/10 text-white/60 hover:text-white rounded-none"
        >
          Șterge Toate Filtrele
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        <title>{String(seo.title)}</title>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <link rel="canonical" href={`https://deprokicks.com/shop${category ? '/' + category : ''}`} />
      </Helmet>

      {/* Header */}
      <div className="py-24 px-6 md:px-12 lg:px-24 border-b border-white/5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="font-body text-xs tracking-[0.3em] text-[#c00000] uppercase">Colecția</span>
          <h1 className="font-heading font-bold text-4xl md:text-6xl tracking-tighter uppercase text-white mt-2" data-testid="shop-title">
            {categoryTitle}
          </h1>
          <p className="font-body text-white/50 mt-2">{products.length} produse</p>
        </motion.div>
      </div>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-72 min-h-screen border-r border-white/5 p-8 sticky top-20 self-start">
          <FilterContent />
        </aside>

        {/* Products Grid */}
        <div className="flex-1 px-6 md:px-12 py-12">
          {/* Mobile Filter Button */}
          <div className="lg:hidden mb-8">
            <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="outline" 
                  className="bg-transparent border-white/10 text-white rounded-none"
                  data-testid="filter-btn"
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  FILTRE
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-full max-w-sm bg-[#0a0a0a] border-white/10">
                <SheetHeader>
                  <SheetTitle className="font-heading text-white">FILTRE</SheetTitle>
                </SheetHeader>
                <div className="mt-8">
                  <FilterContent />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {loading ? (
            <div className="grid grid-cols-4 sm:grid-cols-2 xl:grid-cols-3 gap-y-12 gap-x-2 sm:gap-x-8">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-white/5" />
                  <div className="h-4 bg-white/5 mt-4 w-2/3" />
                  <div className="h-4 bg-white/5 mt-2 w-1/3" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24">
              <h3 className="font-heading text-2xl text-white mb-2">Nu s-au găsit produse</h3>
              <p className="font-body text-white/50">Încearcă să ajustezi filtrele</p>
              <Button 
                onClick={clearFilters}
                className="mt-6 bg-[#c00000] text-white rounded-none"
              >
                Șterge Filtrele
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-2 xl:grid-cols-3 gap-y-12 gap-x-2 sm:gap-x-8">
              {products.map((product, idx) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
